import React, { useState, useEffect } from "react";
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  getDocs,
  doc,
  getDoc
} from "firebase/firestore";
import { db } from "../../firebase/firebase";
import { FiLayout, FiUsers, FiCalendar, FiBriefcase } from "react-icons/fi";
import { IconWrapper } from "../IconWrapper";
import { useAuth } from "../../hooks/useAuth";
import { useColorMode } from "@chakra-ui/react";
import { getThemeClasses } from "../../utils/themeUtils";

// Sub-components
import ManagerFloorView, { Floor, Seat } from "./Manager/ManagerFloorView";
import ManagerAssignmentView from "./Manager/ManagerAssignmentView";
import ManagerBookingList from "./Manager/ManagerBookingList";
import FloorAssignmentManager from "./Manager/FloorAssignmentManager";

// Types
interface Booking {
  id: string;
  seatId: string;
  seatName: string;
  floorId: string;
  floorName: string;
  userId: string;
  userName: string;
  date: string;
  status: 'active' | 'cancelled' | 'completed' | 'pending';
  createdAt: any;
}

interface ManagerBookingProps {
  users?: any[]; // Using any[] to match Dashboard's User type loosely for now, or define a compatible interface
  currentOrgId?: string;
}

const ManagerBooking: React.FC<ManagerBookingProps> = ({ users = [], currentOrgId: propOrgId }) => {
  const { colorMode } = useColorMode();
  const isDark = colorMode === 'dark';
  const theme = getThemeClasses(isDark);
  const [floors, setFloors] = useState<Floor[]>([]);
  const [bookingList, setBookingList] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'floors' | 'date-assignment' | 'assignments' | 'bookings'>('floors');
  
  const { orgId } = useAuth();
  // Use prop if available, else fallback to auth
  const currentOrgId = propOrgId || orgId || "Xyi09suiej75RuHayi"; 

  // Fetch Floors
  useEffect(() => {
    if (!currentOrgId) return;

    const floorsRef = collection(db, `organization/${currentOrgId}/floors`);
    // Use name for sorting to ensure compatibility with existing data
    const q = query(floorsRef, orderBy("name", "asc"));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const floorsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Floor[];
      setFloors(floorsData);
      setLoading(false);
    });

    fetchBookings(); // Initial fetch

    return () => unsubscribe();
  }, [currentOrgId]);
  
  const fetchBookings = async () => {
    if(!currentOrgId) return;
    try {
        const bookingsRef = collection(db, `organization/${currentOrgId}/bookings`);
        // Use client-side filtering/sorting to avoid index issues if needed or specific index isn't ready
        const snap = await getDocs(bookingsRef);
        const books = snap.docs.map(d => ({id: d.id, ...d.data()})) as Booking[];
        books.sort((a,b) => new Date(b.date).getTime() - new Date(a.date).getTime());
        setBookingList(books);
    } catch (e) {
        console.error("Fetch bookings error", e);
    }
  };

  return (
    <div className={`min-h-screen ${theme.background} ${theme.text}`}>
      {/* Premium Header */}
      <div className={`${theme.headerGradient} px-6 py-4 mb-6 rounded-md border ${theme.border}`}>
        {/* Dark mode decorative overlays */}
        {isDark && (
          <>
            <span className={theme.headerOverlayGlow} />
            <span className={theme.headerOverlaySweep} />
            <span className={theme.headerOverlayTopLine} />
            <span className={theme.headerOverlayBottomGlow} />
          </>
        )}
        
        <div className="relative z-10 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2.5 rounded-xl ${isDark ? 'bg-gradient-to-br from-[#FBD282]/20 to-[#B59143]/20 ring-1 ring-[#B59143]/30' : 'bg-gradient-to-br from-[#FBD282] to-[#B59143]'} shadow-lg`}>
              <IconWrapper icon={FiBriefcase} size={22} className={isDark ? 'text-[#FBD282]' : 'text-white'} />
            </div>
            <div>
              <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Booking Management
              </h1>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Manage floors, seats, and user assignments
              </p>
            </div>
          </div>
        </div> 
      </div>

      <div className="px-6">
        {/* Tabs */}
        <div className={`flex gap-4 mb-6 ${isDark ? 'border-b border-[#2E2F33]' : 'border-b border-gray-200'}`}>
          <button
            onClick={() => setActiveTab('floors')}
            className={`pb-2 px-4 text-sm font-semibold transition-colors relative ${
              activeTab === 'floors' 
                ? `${isDark ? 'text-[#FBD282]' : 'text-[#B59143]'} border-b-2 border-[#B59143]` 
                : `${isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'}`
            }`}
          >
            <div className="flex items-center gap-2">
              <IconWrapper icon={FiLayout} size={16} /> Floors & Seats
            </div>
          </button>
          <button
            onClick={() => setActiveTab('date-assignment')}
            className={`pb-2 px-4 text-sm font-semibold transition-colors relative ${
              activeTab === 'date-assignment' 
                ? `${isDark ? 'text-[#FBD282]' : 'text-[#B59143]'} border-b-2 border-[#B59143]` 
                : `${isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'}`
            }`}
          >
            <div className="flex items-center gap-2">
              <IconWrapper icon={FiCalendar} size={16} /> Date Assignment
            </div>
          </button>
          <button
            onClick={() => setActiveTab('assignments')}
            className={`pb-2 px-4 text-sm font-semibold transition-colors relative ${
              activeTab === 'assignments' 
                ? `${isDark ? 'text-[#FBD282]' : 'text-[#B59143]'} border-b-2 border-[#B59143]` 
                : `${isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'}`
            }`}
          >
            <div className="flex items-center gap-2">
              <IconWrapper icon={FiUsers} size={16} /> Manual Assignment
            </div>
          </button>
          <button
            onClick={() => setActiveTab('bookings')}
            className={`pb-2 px-4 text-sm font-semibold transition-colors relative ${
              activeTab === 'bookings' 
                ? `${isDark ? 'text-[#FBD282]' : 'text-[#B59143]'} border-b-2 border-[#B59143]` 
                : `${isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'}`
            }`}
          >
            <div className="flex items-center gap-2">
              <IconWrapper icon={FiCalendar} size={16} /> Bookings
            </div>
          </button>
        </div>

        {/* Content */}
        <div className={`${theme.card} rounded-xl ${isDark ? 'border border-[#2E2F33]' : 'border border-gray-200'} shadow-sm p-6`}>
          {activeTab === 'floors' && (
            <ManagerFloorView 
              floors={floors} 
              currentOrgId={currentOrgId} 
              loading={loading} 
            />
          )}

          {activeTab === 'date-assignment' && (
            <FloorAssignmentManager 
              currentOrgId={currentOrgId}
              floors={floors}
            />
          )}

          {activeTab === 'assignments' && (
            <ManagerAssignmentView 
              floors={floors} 
              currentOrgId={currentOrgId} 
              onRefreshBookings={fetchBookings}
              users={users}
            />
          )}
          
          {activeTab === 'bookings' && (
            <ManagerBookingList 
              bookings={bookingList} 
              floors={floors}
              currentOrgId={currentOrgId}
              onRefresh={fetchBookings}
              users={users}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ManagerBooking;
